/**
 * Bridges the synchronous storage backend (json_store reads/writes profile JSON at logical
 * paths) to DynamoDB, which is the cross-session store for a person's base/run/times data.
 *
 *   - hydrate_session(slug): pull base/run/times from DynamoDB into the in-memory backend so
 *     the existing synchronous `load_user_session(slug)` finds them.
 *   - persist_session(slug): push the three current backend payloads to DynamoDB. Pushes are
 *     SERIALIZED per slug (a promise chain) and re-read the backend at run time, so rapid
 *     edits converge on the final state instead of racing to a stale last-write.
 *
 * The browser's localStorage keeps ONLY the device-local session-name registry (which people
 * this browser can resume) — the profile data itself never persists to localStorage.
 */

import { getStorage } from "./storage";
import { user_paths, user_times_path } from "./json_store";
import { is_unlock_all_url } from "./access_api";
import { build_finals_record } from "./time_compare";
import {
  dynamo_query_user,
  dynamo_put,
  dynamo_delete,
  dynamo_delete_user,
  dynamo_scan_base_profiles,
  dynamo_configured,
  type DynamoType,
} from "./dynamo";

const REGISTRY_PATH = "settings/session_names";

/**
 * The cloud-persisted storage paths for a slug, paired with their DynamoDB sort-key `type`.
 * Only `base` + `run` cross the cloud boundary now: candidate scores (the `times` payload) are
 * EPHEMERAL — recomputed on open against the current events/engine, never persisted — so a
 * reopened profile is always current and we never serve stale scores from another session.
 */
function paths_for(slug: string): { type: DynamoType; path: string }[] {
  const [base_path, run_path] = user_paths(slug);
  return [
    { type: "base", path: base_path },
    { type: "run", path: run_path },
  ];
}

// ── Hydrate / persist ───────────────────────────────────────────────────────────────────────

/**
 * Load a person's items from DynamoDB into the in-memory backend. Returns false (and writes
 * nothing) when the person has no `base` item — i.e. they don't exist in DynamoDB.
 *
 * Runs on the same per-slug queue as persist_session: a hydrate issued while an edit's push
 * is still queued must wait for that push, or stale cloud data would overwrite the backend
 * (and the late push would then re-upload the stale state).
 */
export function hydrate_session(slug: string): Promise<boolean> {
  const prev = _queues.get(slug) ?? Promise.resolve();
  const next = prev.catch(() => {}).then(() => _hydrate_now(slug));
  _queues.set(
    slug,
    next.then(
      () => {},
      () => {},
    ),
  );
  return next;
}

async function _hydrate_now(slug: string): Promise<boolean> {
  const items = await dynamo_query_user(slug);
  if (!items.base) return false;
  const storage = getStorage();
  for (const { type, path } of paths_for(slug)) {
    const payload = items[type];
    if (payload !== undefined) storage.write(path, JSON.stringify(payload, null, 2));
  }
  // Scores are no longer stored in the cloud. Wipe any stale LOCAL times left by a previous
  // session on this browser so load_user_session re-scans fresh instead of showing old scores.
  storage.write(user_times_path(slug), JSON.stringify({ schema_version: 1, user_slug: slug, times: {} }));
  return true;
}

const _queues = new Map<string, Promise<void>>();

async function _push_now(slug: string): Promise<void> {
  const storage = getStorage();
  // base is required; if it isn't in the backend there is nothing to persist.
  const [{ path: base_path }] = paths_for(slug);
  const base_raw = storage.read(base_path);
  if (base_raw === null) return;
  for (const { type, path } of paths_for(slug)) {
    const raw = storage.read(path);
    if (raw === null) continue;
    await dynamo_put(slug, type, JSON.parse(raw));
  }
  // The `finals` decision record — the marked-times comparison outcome — is DERIVED from the base
  // profile and written as its own item so the settled answer is independently queryable. Derive
  // it under a SCOPED guard (only a malformed base profile is swallowed — a real Dynamo write
  // failure below must still reject and raise the sync-error banner, like base/run).
  let base: Record<string, any>;
  try {
    base = JSON.parse(base_raw);
  } catch {
    return; // malformed base profile — base/run already pushed; leave finals untouched
  }
  const finals = build_finals_record(base, new Date().toISOString());
  if (finals) {
    await dynamo_put(slug, "finals", finals);
  } else if (Array.isArray(base.marked_times)) {
    // The shortlist was emptied/reset (marked_times present but empty) — DELETE any prior finals
    // item so a retracted decision never lingers in the cloud. Skipped for profiles that never
    // touched the comparison (no marked_times key → no finals item ever written).
    await dynamo_delete(slug, "finals");
  }
}

/**
 * Persist the slug's current backend state to DynamoDB. Serialized per slug; resolves on
 * success and rejects on failure (callers that fire-and-forget should `.catch`). Also notifies
 * sync-error listeners so the UI can surface a problem without every call site handling it.
 */
export function persist_session(slug: string): Promise<void> {
  const prev = _queues.get(slug) ?? Promise.resolve();
  const next = prev
    .catch(() => {})
    .then(() => _push_now(slug))
    .then(
      () => {
        _notify(null);
      },
      (err) => {
        _notify(err instanceof Error ? err : new Error(String(err)));
        throw err;
      },
    );
  _queues.set(slug, next.catch(() => {}));
  return next;
}

/** Remove a person from DynamoDB entirely (base + run + times). */
export async function delete_session(slug: string): Promise<void> {
  await dynamo_delete_user(slug);
}

// ── Browser session-name registry (device-local, the only persisted browser state) ──────────

export interface SessionName {
  slug: string;
  first_name: string;
}

/** People this browser can resume, sorted by display name. */
export function list_session_names(): SessionName[] {
  const raw = getStorage().read(REGISTRY_PATH);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => x && typeof x.slug === "string") : [];
  } catch {
    return [];
  }
}

export function remember_session(slug: string, first_name: string): void {
  const list = list_session_names().filter((s) => s.slug !== slug);
  list.push({ slug, first_name });
  list.sort((a, b) => a.first_name.localeCompare(b.first_name));
  getStorage().write(REGISTRY_PATH, JSON.stringify(list));
}

/**
 * True ONLY when the app is served from a LOCAL dev origin — the URL hostname is localhost or a
 * loopback address. On any deployed/public origin this is false. It gates the "list everyone in
 * the cloud" feature so a deployed site NEVER exposes the whole roster to visitors; a deployed
 * browser only ever sees the people THIS device created (the local registry).
 *
 * Returns false when `location` is absent (no browser) — so nothing can leak the full list from a
 * non-local context. The cloud-list tests set `location.hostname = "localhost"` to exercise the
 * local path explicitly.
 */
export function is_local_runtime(): boolean {
  const loc = (globalThis as { location?: { hostname?: string } }).location;
  const host = loc?.hostname;
  if (typeof host !== "string" || host === "") return false;
  const h = host.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "[::1]" || h.endsWith(".localhost");
}

/**
 * May this session see the FULL cloud roster (every saved profile), not just this device's own
 * people? True on local dev, OR when the URL hash carries the `+all` unlock (`#<password>+all`) —
 * which lets a trusted operator open any profile from a deployed origin too.
 */
export function can_list_full_roster(): boolean {
  return is_local_runtime() || is_unlock_all_url();
}

/**
 * EVERYONE saved in the cloud table (every `base` item), sorted by display name — so the resume
 * picker can show all people on DynamoDB. **Gated** (`can_list_full_roster`): on a deployed origin
 * WITHOUT the `+all` unlock it returns [] without even querying, so the full roster is never
 * exposed publicly. Also returns [] when DynamoDB is unconfigured or unreachable. Either way the UI
 * falls back to this browser's local registry (the device's own people).
 */
export async function list_cloud_people(): Promise<SessionName[]> {
  if (!can_list_full_roster() || !dynamo_configured()) return [];
  try {
    const people = await dynamo_scan_base_profiles();
    return people
      .map((p) => ({ slug: p.slug, first_name: p.first_name }))
      .sort((a, b) => a.first_name.localeCompare(b.first_name));
  } catch {
    return [];
  }
}

export function forget_session(slug: string): void {
  const list = list_session_names().filter((s) => s.slug !== slug);
  getStorage().write(REGISTRY_PATH, JSON.stringify(list));
}

/**
 * Reconcile this browser's local registry against the cloud: FORGET any locally-remembered person
 * who no longer exists in DynamoDB (e.g. deleted from the admin page, here or on another device).
 * Returns the authoritative cloud roster (sorted) so the caller can show exactly what's on file.
 *
 * Conservative by design — it only ever prunes when it KNOWS the cloud truth:
 *  - Returns null (a no-op, registry untouched) on a deployed origin without the `+all` unlock
 *    (`can_list_full_roster` false, so we never scan the roster publicly), when DynamoDB is
 *    unconfigured, or when the scan ERRORS. A transient network failure must never wipe the list.
 *  - A successful EMPTY scan is authoritative (the table really is empty) and prunes everything.
 */
export async function reconcile_local_with_cloud(): Promise<SessionName[] | null> {
  if (!can_list_full_roster() || !dynamo_configured()) return null;
  let cloud: Array<{ slug: string; first_name: string }>;
  try {
    cloud = await dynamo_scan_base_profiles();
  } catch {
    return null; // unreachable — keep the local list as-is, never prune on an error
  }
  const cloudSlugs = new Set(cloud.map((p) => p.slug));
  for (const local of list_session_names()) {
    if (!cloudSlugs.has(local.slug)) forget_session(local.slug);
  }
  return cloud
    .map((p) => ({ slug: p.slug, first_name: p.first_name }))
    .sort((a, b) => a.first_name.localeCompare(b.first_name));
}

// ── Sync-error notification seam ────────────────────────────────────────────────────────────

type SyncErrorListener = (err: Error | null) => void;
const _listeners = new Set<SyncErrorListener>();

/** Subscribe to persist failures/recoveries (err on failure, null on next success). */
export function on_sync_error(listener: SyncErrorListener): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function _notify(err: Error | null): void {
  for (const listener of _listeners) listener(err);
}
